// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the small interactive pieces of the desktop workspace, shared by the
// list, the board, the side panel and New task: people avatars, the status
// ring that changes status, and the assignee / due / priority / tag / estimate
// editors (menus on the app's Popout). Edits go through KonvoTasks.updateTask,
// which patches every loaded workspace view.

import {openKonvoTask} from '@app/features/konvo_tasks/commands/KonvoTaskCommands';
import {
	dueQuickPicks,
	fmtTimer,
	fromDateAndTime,
	toDateInputValue,
} from '@app/features/konvo_tasks/components/task_sheet/TaskFormat';
import styles from '@app/features/konvo_tasks/components/workspace/desktop/WorkspaceCells.module.css';
import {
	dueTone,
	shortDate,
	statusKeyOf,
} from '@app/features/konvo_tasks/components/workspace/desktop/WorkspaceGrouping';
import {
	MenuDot,
	MenuItem,
	MenuLabel,
	MenuPopout,
	MenuSeparator,
	menuFieldClass,
	menuFieldRowClass,
	useFocusOnMount,
} from '@app/features/konvo_tasks/components/workspace/desktop/WorkspaceMenu';
import {
	PRIORITY_DESCRIPTORS,
	PriorityFlag,
	priorityOf,
	StatusRing,
} from '@app/features/konvo_tasks/components/workspace/shared/WorkspaceBits';
import KonvoTasks, {
	formatDuration,
	type KonvoTask,
	type KonvoTaskListStatus,
	type KonvoTaskPatch,
	type KonvoTaskUser,
	parseDurationInput,
	statusLabel,
} from '@app/features/konvo_tasks/state/KonvoTasks';
import KonvoWorkspace from '@app/features/konvo_tasks/state/KonvoWorkspace';
import {konvoPhotoFor} from '@app/features/konvo_tasks/utils/KonvoPeoplePhotos';
import KonvoClickUp from '@app/features/messaging/state/KonvoClickUp';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {
	CalendarBlankIcon,
	FlagIcon,
	PauseIcon,
	PlayIcon,
	TagIcon,
	UserPlusIcon,
	XIcon,
} from '@app/features/ui/icons/Icons';
import {useClock} from '@app/features/ui/state/Tick';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useState} from 'react';

export type WorkspaceTaskPatch = KonvoTaskPatch;

export const CELL_DESCRIPTORS = {
	saveFailed: msg({
		message: 'Could not save to ClickUp. Try again.',
		comment: 'Tasks workspace: toast when an edit fails.',
	}),
	statusFailed: msg({
		message: "Couldn't change the status — that status may not exist in its list.",
		comment: 'Tasks workspace: toast when a status change fails.',
	}),
	changeStatus: msg({message: 'Change status', comment: 'Tasks workspace: status ring tooltip.'}),
	assignees: msg({message: 'Assignees', comment: 'Tasks workspace: assignee picker tooltip.'}),
	addAssignee: msg({message: 'Add assignee', comment: 'Tasks workspace: empty assignee cell tooltip.'}),
	due: msg({message: 'Due date', comment: 'Tasks workspace: due date picker tooltip.'}),
	priority: msg({message: 'Priority', comment: 'Tasks workspace: priority picker tooltip.'}),
	noPriority: msg({message: 'No priority', comment: 'Tasks workspace: clear priority option / group title.'}),
	tags: msg({message: 'Tags', comment: 'Tasks workspace: tags editor tooltip.'}),
	addTag: msg({message: 'Add a tag…', comment: 'Tasks workspace: tag input placeholder.'}),
	estimate: msg({message: 'Estimate', comment: 'Tasks workspace: time estimate editor.'}),
	estimatePlaceholder: msg({message: 'e.g. 2h 30m', comment: 'Tasks workspace: estimate input placeholder.'}),
	clearDue: msg({message: 'Remove due date', comment: 'Tasks workspace: due menu option.'}),
	clearEstimate: msg({message: 'Remove estimate', comment: 'Tasks workspace: estimate menu option.'}),
	pickDate: msg({message: 'Pick a date', comment: 'Tasks workspace: due menu date field label.'}),
	today: msg({message: 'Today', comment: 'Tasks workspace: due date word.'}),
	tomorrow: msg({message: 'Tomorrow', comment: 'Tasks workspace: due date word.'}),
	nextMonday: msg({message: 'Next Monday', comment: 'Tasks workspace: due quick pick.'}),
	nextWeek: msg({message: 'Next week', comment: 'Tasks workspace: due quick pick.'}),
	logTime: msg({message: 'Log time', comment: 'Tasks workspace time cell: review or manually add tracked time.'}),
	startTimer: msg({message: 'Start timer', comment: 'Tasks workspace: time cell button.'}),
	pauseTimer: msg({message: 'Pause timer', comment: 'Tasks workspace: time cell button.'}),
	timerFailed: msg({message: 'Could not update the timer. Try again.', comment: 'Tasks workspace: timer toast.'}),
	noMembers: msg({message: 'No one to assign yet.', comment: 'Tasks workspace: assignee menu empty state.'}),
	searchPeople: msg({message: 'Search people…', comment: 'Tasks workspace: assignee menu search placeholder.'}),
};

const DUE_PICK_LABELS: Record<'today' | 'tomorrow' | 'monday' | 'week', MessageDescriptor> = {
	today: CELL_DESCRIPTORS.today,
	tomorrow: CELL_DESCRIPTORS.tomorrow,
	monday: CELL_DESCRIPTORS.nextMonday,
	week: CELL_DESCRIPTORS.nextWeek,
};

// ── Edits ────────────────────────────────────────────────────────────────

export async function patchTask(task: KonvoTask, patch: WorkspaceTaskPatch, failed: string): Promise<boolean> {
	if (!task.id) return false;
	try {
		await KonvoTasks.updateTask(task.id, patch);
		return true;
	} catch {
		ToastCommands.createToast({type: 'error', children: failed});
		return false;
	}
}

export async function changeStatus(task: KonvoTask, status: KonvoTaskListStatus, failed: string): Promise<void> {
	try {
		await KonvoWorkspace.setStatus(task, status);
	} catch {
		ToastCommands.createToast({type: 'error', children: failed});
	}
}

/** People who can be assigned in a list: its members, else the workspace members. */
export function membersFor(listId: string | null | undefined): Array<KonvoTaskUser> {
	const meta = listId ? KonvoWorkspace.listMeta.get(listId) : undefined;
	if (meta && meta.members.length > 0) return meta.members;
	return KonvoClickUp.members.map((member) => ({
		id: member.id,
		username: member.username || member.email,
		email: member.email,
		initials: null,
		color: null,
		profile_picture: null,
	}));
}

// ── People ───────────────────────────────────────────────────────────────

function initials(user: KonvoTaskUser): string {
	if (user.initials) return user.initials.slice(0, 2).toUpperCase();
	const parts = (user.username ?? user.email ?? '?').trim().split(/\s+/).filter(Boolean);
	if (parts.length < 2) return (parts[0] ?? '?').slice(0, 2).toUpperCase();
	return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

export function isMe(user: Pick<KonvoTaskUser, 'id'>): boolean {
	const me = KonvoWorkspace.clickupUserId;
	return me != null && String(user.id) === me;
}

export const PersonAvatar = observer(({user, size = 26}: {user: KonvoTaskUser; size?: number}) => {
	const style = {
		'--av': user.color ?? '#87909f',
		width: size,
		height: size,
		fontSize: Math.max(8, Math.round(size * 0.36)),
	} as React.CSSProperties;
	const photo = user.profile_picture ?? konvoPhotoFor(user);
	if (photo) {
		return <img className={styles.avatar} style={style} src={photo} alt="" title={user.username ?? undefined} />;
	}
	return (
		<span
			className={clsx(styles.avatar, isMe(user) && styles.avatarMe)}
			style={style}
			title={user.username ?? undefined}
		>
			{initials(user)}
		</span>
	);
});

export function PeoplePile({
	users,
	max = 3,
	size = 26,
	more = true,
}: {
	users: ReadonlyArray<KonvoTaskUser>;
	max?: number;
	size?: number;
	more?: boolean;
}) {
	if (users.length === 0) return null;
	return (
		<span className={styles.pile}>
			{users.slice(0, max).map((user) => (
				<PersonAvatar key={String(user.id ?? user.username)} user={user} size={size} />
			))}
			{more && users.length > max && <span className={styles.pileMore}>+{users.length - max}</span>}
		</span>
	);
}

// ── Menus ────────────────────────────────────────────────────────────────

export function StatusMenuItems({
	statuses,
	current,
	onPick,
	close,
}: {
	statuses: ReadonlyArray<KonvoTaskListStatus>;
	current: string | null;
	onPick: (status: KonvoTaskListStatus) => void;
	close: () => void;
}) {
	return (
		<>
			{statuses.map((status) => (
				<MenuItem
					key={status.status ?? ''}
					icon={<StatusRing status={status} statuses={[...statuses]} size={15} />}
					label={statusLabel(status.status)}
					checked={statusKeyOf(status) === (current ?? '').toLowerCase()}
					onSelect={() => onPick(status)}
					close={close}
					data-flx="konvo-tasks.workspace.status-option"
				/>
			))}
		</>
	);
}

export const PriorityMenuItems = observer(
	({
		current,
		onPick,
		close,
	}: {
		current: number | null;
		onPick: (priority: number | null) => void;
		close: () => void;
	}) => {
		const {i18n} = useLingui();
		return (
			<>
				{[1, 2, 3, 4].map((id) => (
					<MenuItem
						key={id}
						icon={<PriorityFlag priority={id} />}
						label={i18n._(PRIORITY_DESCRIPTORS[id]!)}
						checked={current === id}
						onSelect={() => onPick(id)}
						close={close}
						data-flx="konvo-tasks.workspace.priority-option"
					/>
				))}
				<MenuSeparator />
				<MenuItem
					icon={<PriorityFlag priority={null} />}
					label={i18n._(CELL_DESCRIPTORS.noPriority)}
					checked={current == null}
					onSelect={() => onPick(null)}
					close={close}
				/>
			</>
		);
	},
);

export const DueMenu = observer(
	({value, onPick, close}: {value: number | null; onPick: (ms: number | null) => void; close: () => void}) => {
		const {i18n} = useLingui();
		const [date, setDate] = useState(toDateInputValue(value));
		return (
			<>
				{dueQuickPicks().map((pick) => (
					<MenuItem
						key={pick.key}
						icon={<CalendarBlankIcon size={16} />}
						label={i18n._(DUE_PICK_LABELS[pick.key])}
						hint={shortDate(pick.ms)}
						onSelect={() => onPick(pick.ms)}
						close={close}
						data-flx="konvo-tasks.workspace.due-option"
					/>
				))}
				<MenuSeparator />
				<MenuLabel>{i18n._(CELL_DESCRIPTORS.pickDate)}</MenuLabel>
				<div className={menuFieldRowClass}>
					<input
						type="date"
						className={menuFieldClass}
						value={date}
						aria-label={i18n._(CELL_DESCRIPTORS.pickDate)}
						onChange={(event) => {
							setDate(event.target.value);
							const ms = fromDateAndTime(event.target.value, '');
							if (ms != null) {
								onPick(ms);
								close();
							}
						}}
						data-flx="konvo-tasks.workspace.due-date"
					/>
				</div>
				{value != null && (
					<MenuItem
						icon={<XIcon size={16} />}
						label={i18n._(CELL_DESCRIPTORS.clearDue)}
						onSelect={() => onPick(null)}
						close={close}
					/>
				)}
			</>
		);
	},
);

export const AssigneeMenuItems = observer(
	({
		members,
		selected,
		onToggle,
	}: {
		members: ReadonlyArray<KonvoTaskUser>;
		selected: ReadonlyArray<KonvoTaskUser>;
		onToggle: (user: KonvoTaskUser, on: boolean) => void;
	}) => {
		const {i18n} = useLingui();
		const [query, setQuery] = useState('');
		const searchRef = useFocusOnMount<HTMLInputElement>();
		const extra = selected.filter((user) => !members.some((member) => member.id === user.id));
		const all = [...extra, ...members];
		const q = query.trim().toLowerCase();
		const shown = all
			.filter((user) => !q || `${user.username ?? ''} ${user.email ?? ''}`.toLowerCase().includes(q))
			.sort((a, b) => {
				const aOn = selected.some((user) => user.id === a.id) ? 0 : 1;
				const bOn = selected.some((user) => user.id === b.id) ? 0 : 1;
				return aOn - bOn || Number(!isMe(a)) - Number(!isMe(b)) || (a.username ?? '').localeCompare(b.username ?? '');
			});
		return (
			<>
				{all.length > 6 && (
					<div className={menuFieldRowClass}>
						<input
							ref={searchRef}
							className={menuFieldClass}
							value={query}
							placeholder={i18n._(CELL_DESCRIPTORS.searchPeople)}
							onChange={(event) => setQuery(event.target.value)}
						/>
					</div>
				)}
				{shown.map((user) => {
					const on = selected.some((entry) => entry.id === user.id);
					return (
						<MenuItem
							key={String(user.id)}
							icon={<PersonAvatar user={user} size={20} />}
							label={user.username ?? user.email ?? '—'}
							checked={on}
							keepOpen
							onSelect={() => onToggle(user, !on)}
							data-flx="konvo-tasks.workspace.assignee-option"
						/>
					);
				})}
				{all.length === 0 && <MenuLabel>{i18n._(CELL_DESCRIPTORS.noMembers)}</MenuLabel>}
			</>
		);
	},
);

export const TagMenu = observer(
	({
		tags,
		suggestions,
		onAdd,
		onRemove,
	}: {
		tags: ReadonlyArray<string>;
		suggestions: ReadonlyArray<string>;
		onAdd: (tag: string) => void;
		onRemove: (tag: string) => void;
	}) => {
		const {i18n} = useLingui();
		const [draft, setDraft] = useState('');
		const inputRef = useFocusOnMount<HTMLInputElement>();
		const q = draft.trim().toLowerCase();
		const offer = suggestions.filter((tag) => !tags.includes(tag) && (!q || tag.toLowerCase().includes(q))).slice(0, 8);
		return (
			<>
				<div className={menuFieldRowClass}>
					<input
						ref={inputRef}
						className={menuFieldClass}
						value={draft}
						placeholder={i18n._(CELL_DESCRIPTORS.addTag)}
						onChange={(event) => setDraft(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter' && draft.trim()) {
								event.preventDefault();
								onAdd(draft.trim().toLowerCase());
								setDraft('');
							}
						}}
						data-flx="konvo-tasks.workspace.tag-input"
					/>
				</div>
				{tags.map((tag) => (
					<MenuItem
						key={`on-${tag}`}
						icon={<TagIcon size={16} />}
						label={tag}
						checked
						keepOpen
						onSelect={() => onRemove(tag)}
					/>
				))}
				{offer.map((tag) => (
					<MenuItem
						key={`off-${tag}`}
						icon={<TagIcon size={16} />}
						label={tag}
						checked={false}
						keepOpen
						onSelect={() => onAdd(tag)}
					/>
				))}
			</>
		);
	},
);

export const EstimateMenu = observer(
	({value, onSet, close}: {value: number | null; onSet: (ms: number | null) => void; close: () => void}) => {
		const {i18n} = useLingui();
		const [draft, setDraft] = useState(value ? formatDuration(value) : '');
		const [invalid, setInvalid] = useState(false);
		const inputRef = useFocusOnMount<HTMLInputElement>();
		return (
			<>
				<MenuLabel>{i18n._(CELL_DESCRIPTORS.estimate)}</MenuLabel>
				<div className={menuFieldRowClass}>
					<input
						ref={inputRef}
						className={clsx(menuFieldClass, invalid && styles.fieldInvalid)}
						value={draft}
						placeholder={i18n._(CELL_DESCRIPTORS.estimatePlaceholder)}
						onChange={(event) => {
							setDraft(event.target.value);
							setInvalid(false);
						}}
						onKeyDown={(event) => {
							if (event.key !== 'Enter') return;
							event.preventDefault();
							const ms = parseDurationInput(draft);
							if (ms == null) {
								setInvalid(true);
								return;
							}
							onSet(ms);
							close();
						}}
						data-flx="konvo-tasks.workspace.estimate-input"
					/>
				</div>
				{value != null && (
					<MenuItem
						icon={<XIcon size={16} />}
						label={i18n._(CELL_DESCRIPTORS.clearEstimate)}
						onSelect={() => onSet(null)}
						close={close}
					/>
				)}
			</>
		);
	},
);

// ── Row cells ────────────────────────────────────────────────────────────

/** The status ring as a button: its menu lists the task's own list statuses. */
export const StatusRingButton = observer(({task, size = 18}: {task: KonvoTask; size?: number}) => {
	const {i18n} = useLingui();
	const statuses = KonvoWorkspace.statusesFor(task.list.id);
	useEffect(() => {
		if (task.list.id) void KonvoWorkspace.loadListMeta(task.list.id);
	}, [task.list.id]);
	return (
		<MenuPopout
			render={(close) => (
				<StatusMenuItems
					statuses={statuses}
					current={task.status.status}
					close={close}
					onPick={(status) => void changeStatus(task, status, i18n._(CELL_DESCRIPTORS.statusFailed))}
				/>
			)}
		>
			<button
				type="button"
				className={styles.ringButton}
				aria-label={i18n._(CELL_DESCRIPTORS.changeStatus)}
				title={`${i18n._(CELL_DESCRIPTORS.changeStatus)} · ${statusLabel(task.status.status)}`}
				data-flx="konvo-tasks.workspace.status-ring"
			>
				<StatusRing status={task.status} statuses={statuses} size={size} />
			</button>
		</MenuPopout>
	);
});

export const AssigneeCell = observer(({task, size = 26}: {task: KonvoTask; size?: number}) => {
	const {i18n} = useLingui();
	return (
		<MenuPopout
			wide
			render={() => (
				<AssigneeMenuItems
					members={membersFor(task.list.id)}
					selected={task.assignees}
					onToggle={(user, on) => {
						if (user.id == null) return;
						void patchTask(
							task,
							{assignees: on ? {add: [user.id]} : {rem: [user.id]}},
							i18n._(CELL_DESCRIPTORS.saveFailed),
						);
					}}
				/>
			)}
		>
			<button
				type="button"
				className={clsx(styles.cellButton, task.assignees.length === 0 && styles.cellEmpty)}
				aria-label={i18n._(task.assignees.length ? CELL_DESCRIPTORS.assignees : CELL_DESCRIPTORS.addAssignee)}
				title={
					task.assignees
						.map((user) => user.username)
						.filter(Boolean)
						.join(', ') || i18n._(CELL_DESCRIPTORS.addAssignee)
				}
				data-flx="konvo-tasks.workspace.assignee-cell"
			>
				{task.assignees.length > 0 ? <PeoplePile users={task.assignees} size={size} /> : <UserPlusIcon size={17} />}
			</button>
		</MenuPopout>
	);
});

/** "Today" / "Tomorrow" bold, overdue red, otherwise "2 Oct". */
export const DueLabel = observer(({task, withIcon = false}: {task: KonvoTask; withIcon?: boolean}) => {
	const {i18n} = useLingui();
	const tone = dueTone(task);
	if (tone === 'none' || task.due_date == null) return null;
	const text =
		tone === 'today'
			? i18n._(CELL_DESCRIPTORS.today)
			: tone === 'tomorrow'
				? i18n._(CELL_DESCRIPTORS.tomorrow)
				: shortDate(task.due_date);
	return (
		<span
			className={clsx(
				styles.due,
				tone === 'overdue' && styles.dueOverdue,
				(tone === 'today' || tone === 'tomorrow') && styles.dueSoon,
			)}
		>
			{withIcon && <CalendarBlankIcon size={15} />}
			{text}
		</span>
	);
});

export const DueCell = observer(({task}: {task: KonvoTask}) => {
	const {i18n} = useLingui();
	return (
		<MenuPopout
			render={(close) => (
				<DueMenu
					value={task.due_date}
					close={close}
					onPick={(ms) =>
						void patchTask(
							task,
							ms == null ? {due_date_ms: null} : {due_date_ms: ms, due_date_time: false},
							i18n._(CELL_DESCRIPTORS.saveFailed),
						)
					}
				/>
			)}
		>
			<button
				type="button"
				className={clsx(styles.cellButton, task.due_date == null && styles.cellEmpty)}
				aria-label={i18n._(CELL_DESCRIPTORS.due)}
				title={i18n._(CELL_DESCRIPTORS.due)}
				data-flx="konvo-tasks.workspace.due-cell"
			>
				{task.due_date != null ? <DueLabel task={task} /> : <CalendarBlankIcon size={17} />}
			</button>
		</MenuPopout>
	);
});

export const PriorityCell = observer(({task, withLabel = true}: {task: KonvoTask; withLabel?: boolean}) => {
	const {i18n} = useLingui();
	const priority = priorityOf(task);
	return (
		<MenuPopout
			render={(close) => (
				<PriorityMenuItems
					current={priority}
					close={close}
					onPick={(next) => void patchTask(task, {priority: next}, i18n._(CELL_DESCRIPTORS.saveFailed))}
				/>
			)}
		>
			<button
				type="button"
				className={clsx(styles.cellButton, styles.priority, priority == null && styles.cellEmpty)}
				aria-label={i18n._(CELL_DESCRIPTORS.priority)}
				title={i18n._(CELL_DESCRIPTORS.priority)}
				data-flx="konvo-tasks.workspace.priority-cell"
			>
				{priority == null ? <FlagIcon size={17} /> : <PriorityFlag priority={priority} />}
				{priority != null && withLabel && <span>{i18n._(PRIORITY_DESCRIPTORS[priority]!)}</span>}
			</button>
		</MenuPopout>
	);
});

/** Tracked time; while this task's timer runs, a live clock and an ink pause button. */
export const TimeCell = observer(({task, split = false}: {task: KonvoTask; split?: boolean}) => {
	const {i18n} = useLingui();
	const [busy, setBusy] = useState(false);
	const running = KonvoTasks.runningEntry;
	const isRunning = running != null && task.id != null && running.task.id === task.id;
	const now = useClock(isRunning);
	const tracked = KonvoTasks.getTime(task.id ?? '')?.total_ms ?? task.time_spent ?? 0;
	const act = async (event: React.MouseEvent) => {
		event.stopPropagation();
		if (busy || !task.id) return;
		setBusy(true);
		try {
			if (isRunning) await KonvoTasks.pauseTimer();
			else await KonvoTasks.startTimer(task.id);
		} catch {
			ToastCommands.createToast({type: 'error', children: i18n._(CELL_DESCRIPTORS.timerFailed)});
		} finally {
			setBusy(false);
		}
	};
	const value = isRunning
		? fmtTimer(Math.max(0, now - (running?.start ?? now)))
		: tracked > 0
			? formatDuration(tracked)
			: '—';
	const canTime = KonvoTasks.timerAvailable !== false && (isRunning || running == null);
	const text = (
		<button
			type="button"
			className={clsx(styles.time, isRunning && styles.timeLive)}
			title={i18n._(CELL_DESCRIPTORS.logTime)}
			aria-label={i18n._(CELL_DESCRIPTORS.logTime)}
			onClick={(event) => {
				event.stopPropagation();
				if (task.id) openKonvoTask(task.id, 'time');
			}}
			data-flx="konvo-tasks.workspace.log-time"
		>
			{value}
		</button>
	);
	const button = canTime ? (
		<button
			type="button"
			className={clsx(styles.play, isRunning && styles.playLive)}
			disabled={busy}
			aria-label={i18n._(isRunning ? CELL_DESCRIPTORS.pauseTimer : CELL_DESCRIPTORS.startTimer)}
			title={i18n._(isRunning ? CELL_DESCRIPTORS.pauseTimer : CELL_DESCRIPTORS.startTimer)}
			onClick={(event) => void act(event)}
			data-flx="konvo-tasks.workspace.timer"
		>
			{isRunning ? <PauseIcon size={13} weight="fill" /> : <PlayIcon size={13} weight="fill" />}
		</button>
	) : (
		<span />
	);
	if (split) {
		return (
			<>
				<span className={styles.timeSlot}>{text}</span>
				<span className={clsx(styles.playSlot, isRunning && styles.playSlotLive)} data-play-slot="">
					{button}
				</span>
			</>
		);
	}
	return (
		<span className={styles.timeInline}>
			{text}
			{button}
		</span>
	);
});

export function TagChip({name, color}: {name: string; color?: string | null}) {
	return (
		<span className={styles.tag} style={color ? ({'--tag': color} as React.CSSProperties) : undefined}>
			{name}
		</span>
	);
}

export {MenuDot};
